import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import type { Id } from '../../../convex/_generated/dataModel'

type ChatContextValue = {
  isOpen: boolean
  selectedPrescriptionId: Id<'prescriptions'> | null
  openChat: (prescriptionId?: Id<'prescriptions'>) => void
  closeChat: () => void
  selectPrescription: (prescriptionId: Id<'prescriptions'> | null) => void
}

const ChatContext = createContext<ChatContextValue | null>(null)

export function ChatProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false)
  const [selectedPrescriptionId, setSelectedPrescriptionId] =
    useState<Id<'prescriptions'> | null>(null)

  const openChat = useCallback((prescriptionId?: Id<'prescriptions'>) => {
    if (prescriptionId) {
      setSelectedPrescriptionId(prescriptionId)
    }
    setIsOpen(true)
  }, [])

  const closeChat = useCallback(() => {
    setIsOpen(false)
  }, [])

  const selectPrescription = useCallback(
    (prescriptionId: Id<'prescriptions'> | null) => {
      setSelectedPrescriptionId(prescriptionId)
    },
    []
  )

  const value = useMemo(
    () => ({
      isOpen,
      selectedPrescriptionId,
      openChat,
      closeChat,
      selectPrescription,
    }),
    [isOpen, selectedPrescriptionId, openChat, closeChat, selectPrescription]
  )

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>
}

export function useChatPanel() {
  const ctx = useContext(ChatContext)
  if (!ctx) {
    throw new Error('useChatPanel must be used within ChatProvider')
  }
  return ctx
}
